import { useEffect } from 'react';

/**
 * Horizontal swipe → next / previous slide, for touch devices.
 *
 * Deliberately conservative, because this element also scrolls VERTICALLY and a
 * gesture that steals ambiguous drags makes reading a long slide infuriating:
 *
 *  • the horizontal distance must clear MIN_DISTANCE, and
 *  • it must exceed the vertical distance by RATIO, so a diagonal thumb-scroll
 *    is read as a scroll, not a page turn, and
 *  • the gesture must finish reasonably quickly — a slow drag is someone
 *    positioning, not flicking, and
 *  • multi-touch is ignored outright (that is a pinch-zoom on a diagram).
 *
 * Listeners are PASSIVE: this never calls preventDefault, so native vertical
 * scrolling and pinch-zoom keep their normal behaviour and the browser is never
 * blocked waiting to find out.
 */
const MIN_DISTANCE = 60;   // px
const RATIO = 1.6;         // horizontal must beat vertical by this much
const MAX_DURATION = 800;  // ms

export function useSwipeNavigation(
  ref: React.RefObject<HTMLElement | null>,
  { onNext, onPrev, enabled = true }: { onNext: () => void; onPrev: () => void; enabled?: boolean },
) {
  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;

    let startX = 0, startY = 0, startT = 0, tracking = false;

    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) { tracking = false; return; }
      const t = e.touches[0];
      startX = t.clientX; startY = t.clientY; startT = Date.now(); tracking = true;
    };

    const onEnd = (e: TouchEvent) => {
      if (!tracking) return;
      tracking = false;
      const t = e.changedTouches[0];
      if (!t) return;
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      if (Date.now() - startT > MAX_DURATION) return;
      if (Math.abs(dx) < MIN_DISTANCE) return;
      if (Math.abs(dx) < Math.abs(dy) * RATIO) return;   // vertical intent wins
      if (dx < 0) onNext(); else onPrev();
    };

    const onCancel = () => { tracking = false; };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchend', onEnd, { passive: true });
    el.addEventListener('touchcancel', onCancel, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onCancel);
    };
  }, [ref, onNext, onPrev, enabled]);
}

export default useSwipeNavigation;
